import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import type { ApiError } from '@/types';

if (!import.meta.env.VITE_API_BASE_URL) {
  throw new Error(
    'VITE_API_BASE_URL is not set. Copy .env.example to .env.local and configure it.',
  );
}

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

/**
 * Shared Axios instance for the CRM Intelligence API.
 * The Auth0 access-token getter is injected at app bootstrap (see AuthTokenBridge)
 * so this module has no direct dependency on React.
 *
 * Token lifecycle:
 *  - Every request calls the injected getter, which wraps Auth0's
 *    `getAccessTokenSilently()`. Auth0 returns the cached token while it is
 *    still valid and transparently refreshes it (refresh token / silent auth)
 *    once it has expired, so a user who leaves the tab open for hours keeps
 *    working without ever seeing a 401.
 *  - If the API still answers 401 (e.g. the cached token was revoked or the
 *    clock drifted), the request is retried exactly once with a token that is
 *    forced to bypass the cache.
 *  - If the token cannot be obtained at all, or the retry is still rejected,
 *    the Auth0 session is genuinely gone. We surface a `sessionExpired`
 *    ApiError and hand off to the injected session-expired handler, which
 *    redirects to login (see AuthTokenBridge).
 */
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

export interface TokenGetterOptions {
  /** Force a network refresh instead of returning the cached token. */
  forceRefresh?: boolean;
}

export type TokenGetter = (options?: TokenGetterOptions) => Promise<string | undefined>;
export type SessionExpiredHandler = () => void;

let getAccessToken: TokenGetter | undefined;
let onSessionExpired: SessionExpiredHandler | undefined;

/** Called from AuthTokenBridge whenever Auth0 state changes. */
export function setAccessTokenGetter(getter: TokenGetter | undefined): void {
  getAccessToken = getter;
}

/** Called from AuthTokenBridge; invoked when silent token refresh is impossible. */
export function setSessionExpiredHandler(handler: SessionExpiredHandler | undefined): void {
  onSessionExpired = handler;
  sessionExpiredNotified = false;
}

/**
 * Auth0 error codes that mean "the user must interactively log in again".
 * Anything else (network blip, rate limit) is treated as transient.
 * @see https://auth0.com/docs/libraries/auth0-single-page-app-sdk#error-codes
 */
const SESSION_EXPIRED_ERRORS = new Set([
  'login_required',
  'consent_required',
  'interaction_required',
  'missing_refresh_token',
  'invalid_grant',
]);

function isSessionExpiredError(err: unknown): boolean {
  const code = (err as { error?: string } | undefined)?.error;
  return typeof code === 'string' && SESSION_EXPIRED_ERRORS.has(code);
}

// Guard so a burst of parallel 401s triggers a single redirect.
let sessionExpiredNotified = false;

function notifySessionExpired(): void {
  if (sessionExpiredNotified) return;
  sessionExpiredNotified = true;
  try {
    onSessionExpired?.();
  } finally {
    // Allow another notification if the redirect did not navigate away
    // (e.g. handler not wired up in tests).
    setTimeout(() => {
      sessionExpiredNotified = false;
    }, 5_000);
  }
}

function sessionExpiredError(message: string): ApiError {
  return { status: 401, message, sessionExpired: true };
}

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  /** Set after the first 401 so we retry at most once. */
  _authRetried?: boolean;
  /** Set by the response interceptor to request a cache-bypassing token. */
  _forceTokenRefresh?: boolean;
}

apiClient.interceptors.request.use(async (config: RetryableRequestConfig) => {
  if (!getAccessToken) return config;

  let token: string | undefined;
  try {
    token = await getAccessToken({ forceRefresh: config._forceTokenRefresh === true });
  } catch (err) {
    if (isSessionExpiredError(err)) {
      // The Auth0 session is gone: do not fire an unauthenticated request that
      // is guaranteed to 401 — send the user back to login instead.
      notifySessionExpired();
      throw sessionExpiredError('Your session has expired. Please sign in again.');
    }
    // Transient failure (network, Auth0 outage). Let the request go out; if the
    // API rejects it the 401 path below retries with a forced refresh.
    console.warn('[api] Could not obtain access token, sending request without it', err);
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ message?: string; details?: string[] }> | ApiError) => {
    // Errors thrown by the request interceptor are already ApiError shaped.
    if (!axios.isAxiosError(error)) {
      return Promise.reject(error);
    }

    const config = error.config as RetryableRequestConfig | undefined;
    const status = error.response?.status;

    if (status === 401 && config && getAccessToken) {
      if (!config._authRetried) {
        // First 401: the token we sent was rejected. Force Auth0 to refresh it
        // (bypassing the cache) and replay the request once.
        config._authRetried = true;
        config._forceTokenRefresh = true;
        try {
          return await apiClient.request(config);
        } catch (retryErr) {
          return Promise.reject(retryErr);
        }
      }

      // Second consecutive 401 with a freshly refreshed token: the session is
      // no longer valid on the server side.
      notifySessionExpired();
      return Promise.reject(sessionExpiredError('Your session has expired. Please sign in again.'));
    }

    const apiError: ApiError = {
      status: status ?? 0,
      message:
        error.response?.data?.message ??
        error.message ??
        'An unexpected error occurred while contacting the CRM Intelligence API.',
      details: error.response?.data?.details,
    };
    return Promise.reject(apiError);
  },
);

/** Serialize array params as repeated keys (?brandIds=1&brandIds=2). */
export function toQueryParams(params: object): URLSearchParams {
  const search = new URLSearchParams();
  Object.entries(params as Record<string, unknown>).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value)) {
      value.forEach((v) => search.append(key, String(v)));
    } else {
      search.append(key, String(value));
    }
  });
  return search;
}
